/** Rótulos de tempo da linha do tempo da conversa.
 *
 * Módulo próprio, sem import de UI, por dois motivos: é calendário puro — a
 * parte onde os erros são de fronteira e só um teste pega — e o `app.ts`
 * arrasta CSS pela cadeia de imports, o que impede testá-lo fora do navegador. */

export const DIAS_SEMANA = [
  "domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado",
];

export const MESES_CURTOS = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

export function meiaNoite(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Manhã, tarde ou noite — os cortes que as pessoas usam quando contam o dia.
 *  Vive aqui para que o marcador e os divisores da conversa falem a mesma
 *  língua: dois jeitos de dividir o dia na mesma tela seria uma tela mentindo. */
export function periodoDoDia(d: Date): "manha" | "tarde" | "noite" {
  const h = d.getHours();
  if (h < 12) return "manha";
  if (h < 18) return "tarde";
  return "noite";
}

const NOME_PERIODO = {
  manha: "de manhã",
  tarde: "à tarde",
  noite: "à noite",
} as const;

/** O rótulo da linha do tempo, que aparece só enquanto se rola a conversa.
 *
 * A granularidade muda com a distância, como no Google Fotos: perto, a hora é
 * o que distingue; longe, ela é ruído e o que importa é o mês ou o ano. Um
 * marcador que dissesse "14:32" numa mensagem de 2024 obrigaria a ler o número
 * inteiro para descobrir que não é de hoje.
 *
 * A conta é por dia de calendário, não por diferença de milissegundos: às
 * 00:10, uma mensagem de "23 horas atrás" é de ontem, e a subtração crua diria
 * que ainda é hoje.
 *
 * Dentro do dia há mais um degrau, e ele é o que faz a escada funcionar: a
 * hora só distingue enquanto se está na mesma parte do dia. Às 18h, uma
 * mensagem das 8h mostrada como "08h" obriga a fazer a conta de cabeça; dizer
 * "de manhã" responde na hora. O corte é a virada de período, não um número de
 * horas arbitrário. */
export function marcadorTempo(ts: number, agora: number = Date.now()): string {
  const d = new Date(ts);
  const n = new Date(agora);
  const dias = Math.round((meiaNoite(n) - meiaNoite(d)) / 86400000);

  if (dias <= 0) {
    const p = periodoDoDia(d);
    if (p === periodoDoDia(n)) return `${String(d.getHours()).padStart(2, "0")}h`;
    return NOME_PERIODO[p];
  }
  if (dias === 1) return "ontem";
  if (dias < 7) return DIAS_SEMANA[d.getDay()]!;
  if (d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()) {
    const dia = String(d.getDate()).padStart(2, "0");
    return `${dia}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  if (d.getFullYear() === n.getFullYear()) return MESES_CURTOS[d.getMonth()]!;
  // Passou do ano: o mês sozinho ficaria ambíguo entre um ano e outro.
  return `${MESES_CURTOS[d.getMonth()]} ${d.getFullYear()}`;
}
